/**
 * Select an area: draw a boundary freehand or corner by corner, inspect the
 * clustered pins, name and save the area, pick a saved one, apply it to the
 * register. Applying hands back the ring plus its saved identity.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { IconArrowBackUp, IconDeviceFloppy, IconMapPin, IconPencil, IconPointer, IconTrash } from '@tabler/icons-react'
import { useDeleteArea, useSaveArea, useSavedAreas } from '../../api/queries'
import type { PropertyMarkerQuery, SavedArea } from '../../api/types'
import { useExperience } from '../../experiences/useExperience'
import { Alert, Button, Dialog, Field, IconButton, TextInput } from '../../ui/kit'
import { RegisterMap, type AreaRing, type DrawMode, type RegisterMapHandle } from './RegisterMap'
import type { LocationArea } from './location'

const plural = (n: number, one: string, many = `${one}s`) => `${n.toLocaleString('en-GB')} ${n === 1 ? one : many}`

export function AreaMapDialog({ open, initialRing, markerQuery, onClose, onApply, onDeleted }: {
  open: boolean
  initialRing: AreaRing
  markerQuery: PropertyMarkerQuery
  onClose: () => void
  onApply: (area: LocationArea) => void
  onDeleted?: (areaId: string) => void
}) {
  const experience = useExperience()
  const areas = useSavedAreas(open)
  const save = useSaveArea()
  const remove = useDeleteArea()
  const [ring, setRing] = useState<AreaRing>(initialRing)
  const [name, setName] = useState('')
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [drawMode, setDrawMode] = useState<DrawMode>(null)
  const [summary, setSummary] = useState({ located: 0, total: 0, unlocated: 0, truncated: false, pending: true, error: false })
  const handle = useRef<RegisterMapHandle | null>(null)

  useEffect(() => { if (open) { setRing(initialRing); setName(''); setSelectedId(null); setDrawMode(null) } }, [open, initialRing])
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && drawMode) { e.stopPropagation(); setDrawMode(null) } }
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  }, [open, drawMode])

  const changeRing = useCallback((next: AreaRing) => { setRing(next); setSelectedId(null) }, [])
  const pick = (area: SavedArea) => { setSelectedId(area.id); setName(area.name); setRing(area.polygon); setDrawMode(null); window.requestAnimationFrame(() => handle.current?.fitRing(area.polygon)) }
  const clear = () => { setRing([]); setName(''); setSelectedId(null); setDrawMode(null) }
  const undo = () => { setSelectedId(null); setDrawMode(null); setRing(r => r.slice(0, -1)) }
  const saveArea = async () => {
    if (ring.length < 3 || !name.trim()) return
    const saved = await save.mutateAsync({ name: name.trim(), polygon: ring })
    setSelectedId(saved.id); setName(saved.name); setRing(saved.polygon)
  }
  const deleteArea = async (area: SavedArea) => { await remove.mutateAsync(area.id); onDeleted?.(area.id); if (selectedId === area.id) clear() }
  const apply = () => { if (ring.length >= 3) onApply({ ring, name: name.trim() || 'Custom area', areaId: selectedId }) }
  const status = summary.pending ? 'Loading properties…' : summary.error ? 'Properties unavailable' : `${plural(summary.located, 'marker')} from ${plural(summary.total, 'match', 'matches')}`

  return (
    <Dialog open={open} onClose={() => (drawMode ? setDrawMode(null) : onClose())} size="full" title="Select an area" subtitle="Draw a boundary and use the clustered markers to inspect matching properties."
      actions={<><Button onClick={onClose}>Cancel</Button><Button kind="primary" disabled={ring.length < 3} onClick={apply}>Apply area</Button></>}>
      <div className="area-body">
        <RegisterMap markerQuery={markerQuery} ring={ring} onRingChange={changeRing} drawMode={drawMode} onDrawModeChange={setDrawMode} spec={experience.spec} enabled={open && experience.ready} onSummary={setSummary} fitTo="ring" handleRef={handle}>
          <div className="map-tools" role="group" aria-label="Map drawing tools">
            <Button small on={drawMode === 'freehand'} onClick={() => setDrawMode(drawMode === 'freehand' ? null : 'freehand')}><IconPencil className="ic-sm" />Draw area</Button>
            <Button small on={drawMode === 'corners'} onClick={() => setDrawMode(drawMode === 'corners' ? null : 'corners')}><IconPointer className="ic-sm" />Add corners</Button>
            <Button small disabled={ring.length === 0} onClick={undo}><IconArrowBackUp className="ic-sm" />Undo</Button>
            <Button small disabled={ring.length === 0} onClick={clear}>Clear</Button>
          </div>
        </RegisterMap>
        <aside className="area-side" aria-label="Area controls">
          <div className="area-sec">
            <span className="t-section">Map results</span>
            <div className="area-stats">
              <span className="chip"><IconMapPin className="ic-sm" aria-hidden="true" />{status}</span>
              {summary.unlocated > 0 ? <span className="chip">{plural(summary.unlocated, 'match', 'matches')} without coordinates</span> : null}
              {summary.truncated ? <span className="chip">limited to 5,000</span> : null}
            </div>
          </div>
          <div className="area-sec">
            <Field label="Area name" hint="Optional; needed to save the shape."><TextInput value={name} onChange={v => { setName(v); if (selectedId) setSelectedId(null) }} placeholder="Saved-area name" /></Field>
            <div className="form-row">
              <Button disabled={ring.length < 3 || !name.trim()} busy={save.isPending} onClick={() => { void saveArea() }}><IconDeviceFloppy className="ic-sm" />Save area</Button>
              <Button kind="primary" disabled={ring.length < 3} onClick={apply}>Use current shape</Button>
            </div>
            {save.isError ? <Alert tight title="Could not save the area">{save.error.message}</Alert> : null}
            {remove.isError ? <Alert tight title="Could not delete the saved area">{remove.error.message}</Alert> : null}
          </div>
          <div className="area-sec">
            <span className="t-section">Saved areas</span>
            <div className="area-list">
              {areas.isPending ? <span className="t-muted">Loading saved areas…</span> : null}
              {areas.isError ? <span className="t-muted">Saved areas unavailable.</span> : null}
              {areas.data && areas.data.length === 0 ? <span className="t-muted">No saved areas yet.</span> : null}
              {(areas.data ?? []).map(area => (
                <div className={['area-row', selectedId === area.id ? 'is-on' : ''].join(' ')} key={area.id}>
                  <button type="button" className="area-pick" onClick={() => pick(area)}><span>{area.name}</span><small>{plural(area.polygon.length, 'point')}</small></button>
                  <IconButton small label={`Delete saved area ${area.name}`} disabled={remove.isPending} onClick={() => { void deleteArea(area) }}><IconTrash className="ic" /></IconButton>
                </div>
              ))}
            </div>
          </div>
        </aside>
      </div>
    </Dialog>
  )
}
